// Remove HTML tags and clean text content while preserving structure
export function cleanHtmlText(text: string | undefined): string {
  if (!text) return "";
  
  let cleaned = text;
  
  // First, convert HTML structure elements to line breaks
  // Paragraph and div tags
  cleaned = cleaned.replace(/<\/p>/gi, '\n\n');
  cleaned = cleaned.replace(/<p[^>]*>/gi, '');
  cleaned = cleaned.replace(/<\/div>/gi, '\n');
  cleaned = cleaned.replace(/<div[^>]*>/gi, '');
  
  // Line breaks
  cleaned = cleaned.replace(/<br\s*\/?>/gi, '\n');
  
  // List items
  cleaned = cleaned.replace(/<li[^>]*>/gi, '• ');
  cleaned = cleaned.replace(/<\/li>/gi, '\n');
  cleaned = cleaned.replace(/<\/ul>/gi, '\n');
  cleaned = cleaned.replace(/<ul[^>]*>/gi, '\n');
  cleaned = cleaned.replace(/<\/ol>/gi, '\n');
  cleaned = cleaned.replace(/<ol[^>]*>/gi, '\n');
  
  // Headers
  cleaned = cleaned.replace(/<\/h[1-6]>/gi, '\n\n');
  cleaned = cleaned.replace(/<h[1-6][^>]*>/gi, '');
  
  // Strong/bold tags - preserve with asterisks for emphasis
  cleaned = cleaned.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
  cleaned = cleaned.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
  
  // Emphasis/italic tags
  cleaned = cleaned.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
  cleaned = cleaned.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
  
  // Remove remaining HTML tags
  cleaned = cleaned.replace(/<[^>]*>/g, '');
  
  // Replace common HTML entities
  cleaned = cleaned
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");
  
  // Clean up excessive line breaks (more than 2 consecutive)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  // Clean up spaces but preserve line breaks
  cleaned = cleaned.split('\n').map(line => line.trim()).join('\n');
  
  // Remove leading/trailing whitespace
  cleaned = cleaned.trim();
  
  return cleaned;
}

// Translate common hotel terms from Portuguese/English to Spanish
export function translateHotelText(text: string): string {
  if (!text) return "";
  
  const translations: Record<string, string> = {
    // Portuguese to Spanish
    'café da manhã': 'desayuno',
    'café da manha': 'desayuno',
    'piscina': 'piscina',
    'estacionamento': 'estacionamiento',
    'restaurante': 'restaurante',
    'quarto': 'habitación',
    'quartos': 'habitaciones',
    'banheiro': 'baño',
    'ar condicionado': 'aire acondicionado',
    'vista para o mar': 'vista al mar',
    'vista para a cidade': 'vista a la ciudad',
    'academia': 'gimnasio',
    'recepção': 'recepción',
    'serviço de quarto': 'servicio de habitaciones',
    'localizado': 'ubicado',
    'oferece': 'ofrece',
    'dispõe': 'dispone',
    'possui': 'tiene',
    'todos os quartos': 'todas las habitaciones',
    'equipado': 'equipado',
    'inclui': 'incluye',
    'próximo': 'cerca',
    
    // English to Spanish
    'hotel': 'hotel',
    'room': 'habitación',
    'rooms': 'habitaciones',
    'breakfast': 'desayuno',
    'wifi': 'wifi',
    'wi-fi': 'wi-fi',
    'pool': 'piscina',
    'swimming pool': 'piscina',
    'parking': 'estacionamiento',
    'restaurant': 'restaurante',
    'bar': 'bar',
    'gym': 'gimnasio',
    'fitness center': 'centro de fitness',
    'spa': 'spa',
    'beach': 'playa',
    'sea view': 'vista al mar',
    'city view': 'vista a la ciudad',
    'air conditioning': 'aire acondicionado',
    'located': 'ubicado',
    'offers': 'ofrece',
    'features': 'dispone',
    'includes': 'incluye',
    'near': 'cerca de',
    'minutes': 'minutos',
    'walk': 'caminata',
    'distance': 'distancia',
    'center': 'centro',
    'airport': 'aeropuerto',
    'reception': 'recepción',
    'room service': 'servicio de habitaciones',
    'free': 'gratis',
    'private': 'privado',
    'bathroom': 'baño',
    'shower': 'ducha',
    'balcony': 'balcón',
    'terrace': 'terraza',
    'all rooms': 'todas las habitaciones',
    'equipped': 'equipado',
  };
  
  let translated = text;
  
  // Sort by length (longest first) to handle multi-word phrases correctly
  const sortedKeys = Object.keys(translations).sort((a, b) => b.length - a.length);
  
  for (const key of sortedKeys) {
    const regex = new RegExp(key, 'gi');
    translated = translated.replace(regex, translations[key]);
  }
  
  return translated;
}

// Clean HTML and translate in one step
export function cleanAndTranslateHotelText(text: string | undefined): string {
  const cleaned = cleanHtmlText(text);
  return translateHotelText(cleaned);
}
